import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output } from '@angular/core';
import { Tables, ScopeResolver } from '../../../../../shared/ui/shared-tables/tables/tables';
import { LoadState } from '../../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { Loading } from '../../../../../utils/loading/loading';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { ActionInterface } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { UserListItem } from '../../../../../services/auth-service.model';
import { SCOPES } from '../../../../../services/scope/scope-constants';
import { toUserTableRows } from './user-table-component.mapper';
import { UserAction } from './user-table-component.model';

const ACTIONS = {
  view: { id: 1, titleEn: 'View User Details', titleAr: 'عرض تفاصيل المستخدم' },
  edit: { id: 2, titleEn: 'Edit User', titleAr: 'تعديل المستخدم' },
  deactivate: { id: 3, titleEn: 'Deactivate User', titleAr: 'إلغاء تفعيل المستخدم' },
  delete: { id: 4, titleEn: 'Delete User', titleAr: 'حذف المستخدم' },
} satisfies Record<string, ActionInterface>;

@Component({
  selector: 'app-user-table-component',
  imports: [Tables, Loading, ErrorData],
  templateUrl: './user-table-component.html',
  styleUrl: './user-table-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserTableComponent {
  private locale = inject(LOCALE_ID);

  /** Users of the current page only. */
  users = input<UserListItem[]>([]);
  state = input<LoadState>('loading');
  isFiltersApplied = input<boolean>(false);

  retry = output<void>();
  actionClick = output<{ action: UserAction; userId: number }>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  rows = computed(() => toUserTableRows(this.users(), this.isArabic(), this.locale));

  headerItems = [
    { key: 'fullName', label: $localize`:@@users.user:User` },
    { key: 'roles', label: $localize`:@@users.role:Role` },
    { key: 'status', label: $localize`:@@users.status:Status`, type: 'status' },
    { key: 'createdOn', label: $localize`:@@users.creationDate:Created on` },
    { key: 'lastUpdated', label: $localize`:@@users.lastUpdated:Last Updated` },
    { key: 'actions', label: '', type: 'actions' },
  ];

  currentActions: ActionInterface[] = Object.values(ACTIONS);

  userScopeResolver: ScopeResolver = (action) => (action.id === ACTIONS.view.id ? SCOPES.USERS.READ_DETAILS : null);

  handleAction(e: { event: Event; actionId: number; entity: { id: number } }) {
    e.event?.stopPropagation();
    const userId = e.entity.id;
    switch (e.actionId) {
      case ACTIONS.view.id:
        this.actionClick.emit({ action: 'View', userId });
        break;
      case ACTIONS.edit.id:
        this.actionClick.emit({ action: 'Edit', userId });
        break;
      case ACTIONS.deactivate.id:
        this.actionClick.emit({ action: 'Deactivate', userId });
        break;
      case ACTIONS.delete.id:
        this.actionClick.emit({ action: 'Delete', userId });
        break;
    }
  }
}
